import { TimeDisplayMode } from "@/shared/components/visualization/WorkspaceCtx";
import { strSecToBigIntNanosec } from "@/shared/time";

const NS_PER_SECOND = 1_000_000_000n;
const NS_PER_MINUTE = 60n * NS_PER_SECOND;
const NS_PER_HOUR = 60n * NS_PER_MINUTE;

/**
 * Accepts user input in a format similar to `Intl.DurationFormat` output
 * with `style: "digital"` — `h:mm:ss` or `h:mm:ss.nnnnnnnnn`.
 *
 * More permissive than the formatter (accepts unbounded hours, optional
 * fractional part with 1–9 digits), but enforces valid minute (0–59) and
 * second (0–59) ranges.
 *
 * Groups: hours, minutes, seconds, optional fractional nanoseconds.
 */
const ELAPSED_TIME_RE = /^(\d+):([0-5]\d):([0-5]\d)(?:\.(\d{1,9}))?$/;

/**
 * Converts a user-entered logtime filter value from its display-mode format
 * to the nanosecond epoch string that the search engine stores internally.
 *
 * - **Log**: seconds string → nanosecond epoch string
 * - **Elapsed**: elapsed seconds string + earliestTime → nanosecond epoch string
 * - **ElapsedTime**: `hh:mm:ss.nnnnnnnnn` → elapsed nanoseconds + earliestTime
 * - **Local / UTC**: passthrough (datetime picker already produces nanosecond strings)
 *
 * **earliestTime caveat**: Elapsed and ElapsedTime modes add `earliestTime`
 * to convert relative offsets to absolute nanosecond epochs. When
 * `earliestTime` is null (workspace time bounds not yet loaded), the offset
 * defaults to 0, producing a value anchored at epoch zero. Callers MUST
 * gate commits via `canCommitLogtime` to avoid creating filters that match
 * nothing.
 *
 * Returns `null` if parsing fails or the value is empty, signaling that the
 * input is invalid and should not be committed.
 */
export function resolveLogTimeValue(
  displayValue: string,
  mode: TimeDisplayMode,
  earliestTime: bigint | null,
): string | null {
  if (displayValue === "") {
    return null;
  }

  switch (mode) {
    case TimeDisplayMode.Local:
    case TimeDisplayMode.UTC:
      return displayValue;

    case TimeDisplayMode.Log:
      return secondsToEpochNanos(displayValue, null);

    case TimeDisplayMode.Elapsed:
      return secondsToEpochNanos(displayValue, earliestTime);

    case TimeDisplayMode.ElapsedTime:
      return resolveElapsedTime(displayValue, earliestTime);
  }
}

/**
 * Converts a seconds string to a nanosecond epoch string, optionally adding
 * an offset (earliestTime). Used by both Log mode (offset = null → 0) and
 * Elapsed mode (offset = earliestTime).
 */
function secondsToEpochNanos(
  value: string,
  offset: bigint | null,
): string | null {
  try {
    const nanos = strSecToBigIntNanosec(value);
    return (nanos + (offset ?? 0n)).toString();
  } catch {
    return null;
  }
}

/**
 * Returns true when a logtime filter is safe to commit given the current
 * time display mode and workspace time bounds. Elapsed / ElapsedTime modes
 * require `earliestTime` to be loaded because their input is relative to
 * that anchor; other modes are always committable.
 *
 * Used by FilterBar's Add button and ColumnFilterPopover's Confirm button
 * to disable commit UI before time bounds arrive.
 */
export function canCommitLogtime(
  mode: TimeDisplayMode,
  earliestTime: bigint | null,
): boolean {
  switch (mode) {
    case TimeDisplayMode.Local:
    case TimeDisplayMode.UTC:
    case TimeDisplayMode.Log:
      return true;
    case TimeDisplayMode.Elapsed:
    case TimeDisplayMode.ElapsedTime:
      return earliestTime !== null;
  }
}

function resolveElapsedTime(
  value: string,
  earliestTime: bigint | null,
): string | null {
  const match = ELAPSED_TIME_RE.exec(value);
  if (match === null) {
    return null;
  }
  const hours = BigInt(match[1]);
  const minutes = BigInt(match[2]);
  const seconds = BigInt(match[3]);
  const fractionalDigits = (match[4] ?? "").padEnd(9, "0");
  const nanos = BigInt(fractionalDigits);

  const elapsedNanos =
    hours * NS_PER_HOUR +
    minutes * NS_PER_MINUTE +
    seconds * NS_PER_SECOND +
    nanos;

  return (elapsedNanos + (earliestTime ?? 0n)).toString();
}
